// Cliente central da API Mosaico.
//
// Substitui as 32 chamadas fetch() espalhadas por PrototypeApp.js.
// Responsabilidades:
// - Base URL padronizada
// - Cookie httpOnly de sessão (credentials: include) é a fonte da identidade
// - Parse de erro consistente (lê o `error` do JSON do backend)
// - Tratamento opcional de 401 (sessão inválida) via onSessionExpired
//
// Uso:
//   const data = await apiGet("/trails", { viewer });
//   const created = await apiPost("/groups", { body: payload, viewer });
//
// O parâmetro `viewer` é mantido por compat com chamadas existentes; ele é
// IGNORADO pela autenticação (o cookie cuida disso). Continua útil só para
// rotas que ainda exigem `viewerUserId` no body — quem o consome é o BODY,
// não o transporte.

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.batistamosaico.com.br/api";

/**
 * Erro lançado pelas funções deste módulo. Carrega status e o `error` do backend.
 */
export class ApiError extends Error {
  constructor(message, { status, code, payload } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

let onSessionExpired = null;

/**
 * Registra um callback que é executado quando a API responde 401 (ou 403 com
 * code SESSION_INVALID). Útil para limpar localStorage e mostrar tela de login.
 */
export function setSessionExpiredHandler(handler) {
  onSessionExpired = typeof handler === "function" ? handler : null;
}

async function parseError(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch (_) {
    /* corpo vazio ou não-JSON */
  }
  const message = payload?.error || `Falha (${response.status})`;
  return new ApiError(message, {
    status: response.status,
    code: payload?.code,
    payload
  });
}

export async function apiFetch(path, options = {}) {
  // viewer continua aceito mas só serve como redundância; cookie é a fonte.
  const { viewer: _viewer, body, headers, skipSessionExpiredHandler, ...rest } = options;
  const finalHeaders = {
    Accept: "application/json",
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(headers || {})
  };

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    credentials: "include", // envia o cookie de sessão httpOnly
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  if (response.status === 401 || response.status === 403) {
    const error = await parseError(response);
    const isSessionInvalid = error.code === "SESSION_INVALID" || response.status === 401;
    // `skipSessionExpiredHandler` existe para chamadas de verificação em segundo
    // plano (ex.: /auth/me no boot do app) cujo 401 é esperado (visitante ainda
    // sem cookie) e não deve derrubar uma sessão mais nova que o próprio chamador
    // já tenha confirmado enquanto esta chamada estava em voo (corrida de rede).
    if (onSessionExpired && isSessionInvalid && !skipSessionExpiredHandler) {
      try {
        onSessionExpired(error);
      } catch (_) {
        /* não deixa o handler quebrar o fluxo */
      }
    }
    throw error;
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}

export const apiGet = (path, options) =>
  apiFetch(path, { ...(options || {}), method: "GET" });

export const apiPost = (path, options) =>
  apiFetch(path, { ...(options || {}), method: "POST" });

export const apiPut = (path, options) =>
  apiFetch(path, { ...(options || {}), method: "PUT" });

export const apiPatch = (path, options) =>
  apiFetch(path, { ...(options || {}), method: "PATCH" });

export const apiDelete = (path, options) =>
  apiFetch(path, { ...(options || {}), method: "DELETE" });
